import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import { EveningReminderForm } from '../components/EveningReminderForm';
import { useEveningReminderSettings } from '../hooks/useEveningReminderSettings';
import { mapUpdateError } from '../lib/updateError';
import type { EveningReminderFormValues } from '../types';
import styles from './EveningReminderSettingsScreen.module.css';

// PARA-136 — экран настроек вечернего напоминания: загрузка, сохранение,
// повторная попытка, не сброс формы при ошибке (AC-E2).
export function EveningReminderSettingsScreen(): JSX.Element {
  const { t } = useTranslation('settings');
  const { settings, updateSettings, isSaving } = useEveningReminderSettings();

  // Форма инициализируется ровно один раз при первой успешной загрузке:
  // повторные фоновые обновления и ошибки мутации форму не сбрасывают.
  const [initialValues, setInitialValues] = useState<EveningReminderFormValues | null>(null);

  useEffect(() => {
    if (settings.data !== undefined && initialValues === null) {
      setInitialValues({
        evening_reminder_enabled: settings.data.evening_reminder_enabled,
        evening_reminder_time: settings.data.evening_reminder_time,
        evening_reminder_timezone: settings.data.evening_reminder_timezone,
      });
    }
  }, [settings.data, initialValues]);

  const handleSubmit = (values: EveningReminderFormValues): void => {
    updateSettings.reset();
    updateSettings.mutate(values);
  };

  if (settings.isError && initialValues === null) {
    return (
      <div className={styles.screen}>
        <h1 className={styles.title}>{t('screen.title')}</h1>
        <p className={styles.message}>{t('errors.load')}</p>
        <div className={styles.messageAction}>
          <Button variant="secondary" fullWidth onClick={() => void settings.refetch()}>
            {t('errors.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (initialValues === null) {
    return (
      <div className={styles.screen}>
        <h1 className={styles.title}>{t('screen.title')}</h1>
        <div className={styles.skeleton} data-testid="settings-skeleton" />
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <h1 className={styles.title}>{t('screen.title')}</h1>
      <EveningReminderForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isSaving={isSaving}
        serverErrors={mapUpdateError(updateSettings.error)}
      />
    </div>
  );
}
